/**
 * FamilyManagementTabs.jsx — Four new tab panels for the Family
 * Management Centre:
 *   • FamilyPhotosTab      — gallery of contributions (photo/video/doc/message)
 *   • MeetingMinutesTab    — CRUD + comment thread
 *   • CharityTab           — CRUD + contributor log + updates timeline
 *   • CommentsNotesTab     — topic-indexed notes, per-member & per-date
 *
 * These components stand alone from the giant FamilyManagementCenter.jsx
 * so the parent module stays under its complexity budget. They share
 * the same design language (wofy-card, wofy-input, wofy-btn-primary).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Images, Video, FileText, MessageSquare, Loader2, X, Eye, EyeOff,
  Trash2, Calendar, Plus, MessageCircle, HandCoins, StickyNote,
  ChevronRight, Filter, Send, Tag, Users as UsersIcon, Search,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";

/* ============================================================
 *  Family Photos tab
 * ============================================================ */
const KIND_META = {
  photo:    { label: "Photo",    Icon: Images,        color: "#DB2777" },
  video:    { label: "Video",    Icon: Video,         color: "#DC2626" },
  document: { label: "Document", Icon: FileText,      color: "#0891B2" },
  message:  { label: "Note",     Icon: MessageSquare, color: "#7C3AED" },
};

export function FamilyPhotosTab({ treeId, isAdmin }) {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("");
  const [includeHidden, setIncludeHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (isAdmin && includeHidden) params.include_hidden = true;
      const { data } = await http.get(`/family-trees/${treeId}/contributions`, { params });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId, kind, includeHidden]);

  const remove = async (id) => {
    if (!window.confirm("Delete this contribution?")) return;
    try {
      await http.delete(`/family-trees/${treeId}/contributions/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };
  const toggleHide = async (row) => {
    try {
      await http.patch(`/family-trees/${treeId}/contributions/${row.id}`, { hidden: !row.hidden });
      setItems((prev) => prev.map((x) => (x.id === row.id ? { ...x, hidden: !row.hidden } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };

  return (
    <div className="space-y-4" data-testid="fmc-family-photos">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs opacity-60 mr-1">Filter:</span>
        {["", "photo", "video", "document", "message"].map((k) => (
          <button
            key={k || "all"}
            onClick={() => setKind(k)}
            className={`text-xs px-3 py-1.5 rounded-full border ${kind === k ? "font-semibold" : ""}`}
            style={kind === k
              ? { borderColor: "#4338CA", color: "#4338CA", background: "#EEF2FF" }
              : { borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
            data-testid={`fmc-photos-filter-${k || "all"}`}
          >
            {k ? KIND_META[k].label + "s" : "All"}
          </button>
        ))}
        {isAdmin && (
          <label className="ml-auto text-xs flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={includeHidden}
              onChange={(e) => setIncludeHidden(e.target.checked)}
              data-testid="fmc-photos-include-hidden"
            /> Show hidden items
          </label>
        )}
      </div>

      {busy && items == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      {items && items.length === 0 && (
        <div className="wofy-card text-sm opacity-70">
          Nothing here yet. Family members can contribute photos, videos, documents, or notes from a member's profile page.
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(items || []).map((row) => (
          <ContributionCard key={row.id} row={row} isAdmin={isAdmin} onDelete={() => remove(row.id)} onToggleHide={() => toggleHide(row)} />
        ))}
      </div>
    </div>
  );
}

function ContributionCard({ row, isAdmin, onDelete, onToggleHide }) {
  const meta = KIND_META[row.kind] || KIND_META.message;
  const { Icon } = meta;
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--wofy-border)", background: "#FFFFFF" }} data-testid={`fmc-contrib-${row.id}`}>
      {row.kind === "photo" && row.url && (
        <img src={row.url} alt={row.caption || "Contribution"} className="w-full aspect-square object-cover" loading="lazy" />
      )}
      {row.kind === "video" && (
        <div className="aspect-video bg-black flex items-center justify-center">
          {row.embed_url ? (
            <iframe src={row.embed_url} title={row.caption || "Video"} className="w-full h-full" allowFullScreen />
          ) : (
            <a href={row.url} target="_blank" rel="noreferrer" className="text-white text-xs underline">Open video</a>
          )}
        </div>
      )}
      {row.kind === "document" && (
        <a href={row.url} target="_blank" rel="noreferrer" className="block bg-slate-50 aspect-video flex items-center justify-center gap-2 hover:bg-slate-100">
          <FileText className="w-8 h-8" style={{ color: meta.color }} />
          <span className="text-sm underline">Open document</span>
        </a>
      )}
      {row.kind === "message" && (
        <div className="p-3 bg-violet-50 text-sm whitespace-pre-wrap">{row.body}</div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-1 text-[10px] font-mono uppercase mb-1" style={{ color: meta.color }}>
          <Icon className="w-3 h-3" /> {meta.label}
          {row.hidden && <span className="ml-1 text-red-600 font-semibold">HIDDEN</span>}
        </div>
        {row.caption && <div className="text-sm mb-1">{row.caption}</div>}
        {row.member && (
          <Link
            to={`/family/${row.tree_id}/member/${row.member.id}`}
            className="text-xs inline-flex items-center gap-1 mt-1"
            style={{ color: "#4338CA" }}
            data-testid={`fmc-contrib-member-link-${row.id}`}
          >
            <UsersIcon className="w-3 h-3" /> Tagged: {row.member.name}
          </Link>
        )}
        <div className="text-[11px] opacity-60 mt-2 flex items-center justify-between">
          <span>by {row.uploader_name}{row.uploader_is_admin ? " · admin" : ""}</span>
          <span>{new Date(row.created_at).toLocaleDateString()}</span>
        </div>
        {isAdmin && (
          <div className="mt-2 flex items-center gap-2">
            <button onClick={onToggleHide} className="text-[11px] opacity-70 hover:opacity-100 flex items-center gap-1" data-testid={`fmc-contrib-hide-${row.id}`}>
              {row.hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide</>}
            </button>
            <button onClick={onDelete} className="text-[11px] text-red-600 hover:text-red-700 flex items-center gap-1" data-testid={`fmc-contrib-del-${row.id}`}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
 *  Meeting Minutes tab
 * ============================================================ */
export function MeetingMinutesTab({ treeId, isAdmin }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/meetings`);
      setItems(data.items || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId]);

  return (
    <div className="space-y-4" data-testid="fmc-meetings">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">MEETING MINUTES</div>
          <h2 className="font-display text-xl">Reunions · Committee · Events</h2>
        </div>
        <button className="wofy-btn-primary text-sm" onClick={() => setShowForm(true)} data-testid="fmc-meetings-new">
          <Plus className="w-4 h-4" /> New meeting
        </button>
      </div>
      {items == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      {items && items.length === 0 && (
        <div className="wofy-card text-sm opacity-70">No meetings yet. Log your first family reunion, committee meeting or event.</div>
      )}
      <div className="space-y-2">
        {(items || []).map((m) => (
          <button
            key={m.id}
            onClick={() => setSelected(m.id)}
            className="w-full text-left wofy-card hover:shadow-md transition"
            data-testid={`fmc-meeting-row-${m.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{m.title}</div>
                <div className="text-xs opacity-70 mt-1 flex flex-wrap items-center gap-3">
                  {m.meeting_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {m.meeting_date}</span>}
                  <span>by {m.author_name}</span>
                  {m.comment_count > 0 && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {m.comment_count}</span>}
                </div>
                {m.description && <div className="text-sm mt-1 opacity-80 line-clamp-2">{m.description}</div>}
              </div>
              <ChevronRight className="w-4 h-4 opacity-40 flex-shrink-0 mt-1" />
            </div>
          </button>
        ))}
      </div>
      {showForm && <MeetingForm treeId={treeId} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {selected && <MeetingDetail treeId={treeId} meetingId={selected} isAdmin={isAdmin} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function MeetingForm({ treeId, onClose, onCreated, initial }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [meetingDate, setMeetingDate] = useState(initial?.meeting_date || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [tags, setTags] = useState((initial?.tags || []).join(", "));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2) return toast.error("Add a title.");
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        meeting_date: meetingDate || undefined,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
        tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (initial) {
        await http.patch(`/family-trees/${treeId}/meetings/${initial.id}`, payload);
      } else {
        await http.post(`/family-trees/${treeId}/meetings`, payload);
      }
      toast.success(initial ? "Meeting updated." : "Meeting saved.");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={initial ? "Edit meeting" : "New meeting"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="fmc-meeting-form">
        <FormRow label="Title *">
          <input className="wofy-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={240} data-testid="fmc-meeting-title" />
        </FormRow>
        <FormRow label="Meeting date">
          <input type="date" className="wofy-input" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} data-testid="fmc-meeting-date" />
        </FormRow>
        <FormRow label="Short description">
          <textarea className="wofy-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} data-testid="fmc-meeting-desc" />
        </FormRow>
        <FormRow label="Long-form notes (any language)">
          <textarea dir="auto" className="wofy-input" rows={8} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={60000} placeholder="Discussion, decisions, action items — English, മലയാളം, हिन्दी, தமிழ், anything." data-testid="fmc-meeting-notes" />
        </FormRow>
        <FormRow label="Tags (comma-separated)">
          <input className="wofy-input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g. reunion-2026, committee, kudumba-yogam" data-testid="fmc-meeting-tags" />
        </FormRow>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-meeting-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save meeting"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function MeetingDetail({ treeId, meetingId, isAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/meetings/${meetingId}`);
      setData(data);
    } catch { setData({ meeting: null }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);
  const addComment = async (e) => {
    e.preventDefault();
    if (newComment.trim().length < 1) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/meetings/${meetingId}/comments`, { body: newComment.trim() });
      setNewComment("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't comment.");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this meeting?")) return;
    try {
      await http.delete(`/family-trees/${treeId}/meetings/${meetingId}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };
  if (!data) return <ModalShell title="Loading…" onClose={onClose}><Loader2 className="w-5 h-5 animate-spin mx-auto" /></ModalShell>;
  if (!data.meeting) return <ModalShell title="Not found" onClose={onClose}><p className="opacity-70">This meeting no longer exists.</p></ModalShell>;
  const m = data.meeting;
  if (editing) return <MeetingForm treeId={treeId} initial={m} onClose={() => setEditing(false)} onCreated={() => { setEditing(false); load(); }} />;
  return (
    <ModalShell title={m.title} subtitle={m.meeting_date} onClose={onClose} widthClass="max-w-2xl">
      <div className="space-y-4" data-testid="fmc-meeting-detail">
        {m.description && <p className="text-sm opacity-80">{m.description}</p>}
        {m.notes && <div className="text-sm whitespace-pre-wrap wofy-card" style={{ background: "#FAFAF7" }}>{m.notes}</div>}
        {(m.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {m.tags.map((t) => <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700"># {t}</span>)}
          </div>
        )}
        <div className="text-xs opacity-60">Logged by {m.author_name} · {new Date(m.created_at).toLocaleString()}</div>
        {data.can_edit && (
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="wofy-btn-ghost text-xs" data-testid="fmc-meeting-edit">Edit</button>
            <button onClick={del} className="wofy-btn-ghost text-xs text-red-600" data-testid="fmc-meeting-del">Delete</button>
          </div>
        )}

        <div>
          <div className="overline mb-2">DISCUSSION ({(data.comments || []).length})</div>
          <div className="space-y-2 mb-3">
            {(data.comments || []).map((c) => (
              <div key={c.id} className="text-sm border-l-2 pl-3" style={{ borderColor: c.author_is_admin ? "#4338CA" : "var(--wofy-border)" }}>
                <div className="text-[11px] opacity-60 mb-0.5">{c.author_name}{c.author_is_admin ? " · admin" : ""} · {new Date(c.created_at).toLocaleString()}</div>
                <div className="whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
            {(data.comments || []).length === 0 && <div className="text-xs opacity-60">Be the first to comment.</div>}
          </div>
          <form onSubmit={addComment} className="flex gap-2">
            <input dir="auto" className="wofy-input flex-1" placeholder="Add a comment…" value={newComment} onChange={(e) => setNewComment(e.target.value)} data-testid="fmc-meeting-comment-input" />
            <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-meeting-comment-submit">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </ModalShell>
  );
}

/* ============================================================
 *  Charity tab
 * ============================================================ */
const CHARITY_STATUS = {
  open:   { label: "Open",   color: "#059669", bg: "#ECFDF5" },
  funded: { label: "Funded", color: "#2563EB", bg: "#EFF6FF" },
  closed: { label: "Closed", color: "#6B7280", bg: "#F3F4F6" },
};

export function CharityTab({ treeId, isAdmin }) {
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await http.get(`/family-trees/${treeId}/charity`, { params });
      setItems(data.items || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId, statusFilter]);

  return (
    <div className="space-y-4" data-testid="fmc-charity">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">CHARITY</div>
          <h2 className="font-display text-xl">Requests · Activities · Contributions</h2>
        </div>
        <button className="wofy-btn-primary text-sm" onClick={() => setShowForm(true)} data-testid="fmc-charity-new">
          <Plus className="w-4 h-4" /> New charity item
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {["", "open", "funded", "closed"].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "font-semibold" : ""}`}
            style={statusFilter === s
              ? { borderColor: "#4338CA", color: "#4338CA", background: "#EEF2FF" }
              : { borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
            data-testid={`fmc-charity-filter-${s || "all"}`}
          >
            {s ? CHARITY_STATUS[s].label : "All"}
          </button>
        ))}
      </div>
      {items == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
      {items && items.length === 0 && (
        <div className="wofy-card text-sm opacity-70">No charity activities logged yet. Track family-funded charities, requests, and drives here.</div>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {(items || []).map((c) => {
          const meta = CHARITY_STATUS[c.status] || CHARITY_STATUS.open;
          const pct = c.amount_needed ? Math.min(100, Math.round((c.amount_raised || 0) / c.amount_needed * 100)) : null;
          return (
            <button
              key={c.id}
              onClick={() => setSelected(c.id)}
              className="text-left wofy-card hover:shadow-md transition"
              data-testid={`fmc-charity-row-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-medium flex items-center gap-2">
                  <HandCoins className="w-4 h-4" style={{ color: meta.color }} /> {c.title}
                </div>
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>
                  {meta.label}
                </span>
              </div>
              {c.beneficiary && <div className="text-xs opacity-70 mb-1">Beneficiary: {c.beneficiary}</div>}
              {c.description && <div className="text-sm opacity-80 line-clamp-2 mb-2">{c.description}</div>}
              {c.amount_needed != null && (
                <div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-2" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                  <div className="text-[11px] opacity-70 mt-1">
                    {c.currency} {(c.amount_raised || 0).toLocaleString()} / {c.amount_needed.toLocaleString()} raised ({pct}%)
                  </div>
                </div>
              )}
              <div className="text-[11px] opacity-60 mt-2">by {c.author_name} · {new Date(c.created_at).toLocaleDateString()}</div>
            </button>
          );
        })}
      </div>
      {showForm && <CharityForm treeId={treeId} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {selected && <CharityDetail treeId={treeId} charityId={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function CharityForm({ treeId, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    beneficiary: initial?.beneficiary || "",
    description: initial?.description || "",
    amount_needed: initial?.amount_needed || "",
    currency: initial?.currency || "INR",
    status: initial?.status || "open",
    tags: (initial?.tags || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.title.trim().length < 2) return toast.error("Add a title.");
    setBusy(true);
    try {
      const payload = {
        title: f.title.trim(),
        beneficiary: f.beneficiary.trim() || undefined,
        description: f.description.trim() || undefined,
        amount_needed: f.amount_needed ? parseFloat(f.amount_needed) : null,
        currency: f.currency,
        status: f.status,
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (initial) {
        await http.patch(`/family-trees/${treeId}/charity/${initial.id}`, payload);
      } else {
        await http.post(`/family-trees/${treeId}/charity`, payload);
      }
      toast.success(initial ? "Charity item updated." : "Charity item created.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };
  const upd = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  return (
    <ModalShell title={initial ? "Edit charity item" : "New charity item"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="fmc-charity-form">
        <FormRow label="Title *">
          <input className="wofy-input" value={f.title} onChange={upd("title")} maxLength={240} data-testid="fmc-charity-title" />
        </FormRow>
        <FormRow label="Beneficiary">
          <input className="wofy-input" value={f.beneficiary} onChange={upd("beneficiary")} data-testid="fmc-charity-beneficiary" />
        </FormRow>
        <FormRow label="Description">
          <textarea className="wofy-input" rows={4} value={f.description} onChange={upd("description")} maxLength={8000} data-testid="fmc-charity-desc" />
        </FormRow>
        <div className="grid grid-cols-3 gap-2">
          <FormRow label="Amount needed">
            <input type="number" min="0" step="0.01" className="wofy-input" value={f.amount_needed} onChange={upd("amount_needed")} data-testid="fmc-charity-amount" />
          </FormRow>
          <FormRow label="Currency">
            <select className="wofy-input" value={f.currency} onChange={upd("currency")} data-testid="fmc-charity-currency">
              <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option><option>AUD</option><option>CAD</option>
            </select>
          </FormRow>
          <FormRow label="Status">
            <select className="wofy-input" value={f.status} onChange={upd("status")} data-testid="fmc-charity-status">
              <option value="open">Open</option>
              <option value="funded">Funded</option>
              <option value="closed">Closed</option>
            </select>
          </FormRow>
        </div>
        <FormRow label="Tags (comma-separated)">
          <input className="wofy-input" value={f.tags} onChange={upd("tags")} data-testid="fmc-charity-tags" />
        </FormRow>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-charity-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function CharityDetail({ treeId, charityId, onClose }) {
  const [data, setData] = useState(null);
  const [update, setUpdate] = useState("");
  const [busy, setBusy] = useState(false);
  const [contribName, setContribName] = useState("");
  const [contribAmt, setContribAmt] = useState("");
  const [contribNote, setContribNote] = useState("");
  const [editing, setEditing] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/charity/${charityId}`);
      setData(data);
    } catch { setData({ charity: null }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [charityId]);
  const addUpdate = async (e) => {
    e.preventDefault();
    if (update.trim().length < 1) return;
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/charity/${charityId}/updates`, { body: update.trim() });
      setUpdate("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post update.");
    } finally { setBusy(false); }
  };
  const addContrib = async (e) => {
    e.preventDefault();
    if (contribName.trim().length < 1) return toast.error("Contributor name required");
    setBusy(true);
    try {
      await http.post(`/family-trees/${treeId}/charity/${charityId}/contributors`, {
        name: contribName.trim(),
        amount: contribAmt ? parseFloat(contribAmt) : null,
        note: contribNote.trim() || null,
      });
      setContribName(""); setContribAmt(""); setContribNote("");
      await load();
      toast.success("Contribution logged.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log.");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this charity item?")) return;
    try {
      await http.delete(`/family-trees/${treeId}/charity/${charityId}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };
  if (!data) return <ModalShell title="Loading…" onClose={onClose}><Loader2 className="w-5 h-5 animate-spin mx-auto" /></ModalShell>;
  if (!data.charity) return <ModalShell title="Not found" onClose={onClose}><p className="opacity-70">This charity item no longer exists.</p></ModalShell>;
  const c = data.charity;
  const meta = CHARITY_STATUS[c.status] || CHARITY_STATUS.open;
  if (editing) return <CharityForm treeId={treeId} initial={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />;
  return (
    <ModalShell title={c.title} onClose={onClose} widthClass="max-w-2xl">
      <div className="space-y-4" data-testid="fmc-charity-detail">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
          {c.beneficiary && <span className="text-xs opacity-70">for {c.beneficiary}</span>}
        </div>
        {c.description && <p className="text-sm opacity-80 whitespace-pre-wrap">{c.description}</p>}
        {c.amount_needed != null && (
          <div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-3" style={{ width: `${Math.min(100, ((c.amount_raised || 0) / c.amount_needed) * 100)}%`, background: meta.color }} />
            </div>
            <div className="text-xs opacity-70 mt-1">{c.currency} {(c.amount_raised || 0).toLocaleString()} / {c.amount_needed.toLocaleString()}</div>
          </div>
        )}
        {(c.contributors || []).length > 0 && (
          <div>
            <div className="overline mb-1">CONTRIBUTORS ({c.contributors.length})</div>
            <ul className="text-sm space-y-1">
              {c.contributors.map((x) => (
                <li key={x.id} className="flex justify-between border-b py-1" style={{ borderColor: "var(--wofy-border)" }}>
                  <span>{x.name}{x.note ? ` — ${x.note}` : ""}</span>
                  <span className="opacity-70">{x.amount != null ? `${c.currency} ${x.amount.toLocaleString()}` : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data.can_edit && (
          <>
            <form onSubmit={addContrib} className="wofy-card" style={{ background: "#F9FAFB" }}>
              <div className="overline mb-2">LOG A CONTRIBUTION</div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <input className="wofy-input" placeholder="Name" value={contribName} onChange={(e) => setContribName(e.target.value)} data-testid="fmc-charity-contrib-name" />
                <input className="wofy-input" placeholder="Amount" type="number" min="0" step="0.01" value={contribAmt} onChange={(e) => setContribAmt(e.target.value)} data-testid="fmc-charity-contrib-amount" />
                <input className="wofy-input" placeholder="Note (optional)" value={contribNote} onChange={(e) => setContribNote(e.target.value)} data-testid="fmc-charity-contrib-note" />
              </div>
              <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-charity-contrib-submit">Log</button>
            </form>
            <div className="flex gap-2">
              <button onClick={() => setEditing(true)} className="wofy-btn-ghost text-xs" data-testid="fmc-charity-edit">Edit item</button>
              <button onClick={del} className="wofy-btn-ghost text-xs text-red-600" data-testid="fmc-charity-del">Delete item</button>
            </div>
          </>
        )}
        <div>
          <div className="overline mb-2">TIMELINE / UPDATES</div>
          <div className="space-y-2 mb-3">
            {(data.updates || []).map((u) => (
              <div key={u.id} className="text-sm border-l-2 pl-3" style={{ borderColor: u.author_is_admin ? "#4338CA" : "var(--wofy-border)" }}>
                <div className="text-[11px] opacity-60">{u.author_name}{u.author_is_admin ? " · admin" : ""} · {new Date(u.created_at).toLocaleString()}</div>
                <div className="whitespace-pre-wrap">{u.body}</div>
              </div>
            ))}
            {(data.updates || []).length === 0 && <div className="text-xs opacity-60">No updates yet.</div>}
          </div>
          <form onSubmit={addUpdate} className="flex gap-2">
            <input dir="auto" className="wofy-input flex-1" placeholder="Post an update…" value={update} onChange={(e) => setUpdate(e.target.value)} data-testid="fmc-charity-update-input" />
            <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-charity-update-submit"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      </div>
    </ModalShell>
  );
}

/* ============================================================
 *  Comments / Notes tab (topic-indexed)
 * ============================================================ */
export function CommentsNotesTab({ treeId, isAdmin }) {
  const [items, setItems] = useState(null);
  const [topics, setTopics] = useState({});
  const [topic, setTopic] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adminOnly, setAdminOnly] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (topic) params.topic = topic;
      if (adminOnly) params.admin_only = true;
      const { data } = await http.get(`/family-trees/${treeId}/notes`, { params });
      setItems(data.items || []);
      setTopics(data.topics || {});
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [treeId, topic, adminOnly]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((n) =>
      (n.title || "").toLowerCase().includes(q) ||
      (n.details || "").toLowerCase().includes(q) ||
      (n.topic || "").toLowerCase().includes(q) ||
      (n.member?.name || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-4" data-testid="fmc-notes">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">COMMENTS · NOTES</div>
          <h2 className="font-display text-xl">Topic-indexed archive</h2>
        </div>
        <button className="wofy-btn-primary text-sm" onClick={() => setShowForm(true)} data-testid="fmc-notes-new">
          <Plus className="w-4 h-4" /> New note
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <aside className="md:col-span-1 space-y-3">
          <div className="wofy-card p-3">
            <div className="text-xs font-medium mb-2 flex items-center gap-1"><Search className="w-3 h-3" /> Search</div>
            <input className="wofy-input" placeholder="Search notes…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="fmc-notes-search" />
          </div>
          <div className="wofy-card p-3">
            <div className="text-xs font-medium mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Topics</div>
            <button
              onClick={() => setTopic("")}
              className="w-full text-left text-sm px-2 py-1 rounded hover:bg-slate-50 flex justify-between"
              style={topic === "" ? { fontWeight: 600, color: "#4338CA" } : {}}
              data-testid="fmc-notes-topic-all"
            >
              <span>All topics</span>
              <span className="opacity-60">{Object.values(topics).reduce((a, b) => a + b, 0)}</span>
            </button>
            {Object.entries(topics).sort((a, b) => b[1] - a[1]).map(([t, n]) => (
              <button
                key={t}
                onClick={() => setTopic(t)}
                className="w-full text-left text-sm px-2 py-1 rounded hover:bg-slate-50 flex justify-between"
                style={topic === t ? { fontWeight: 600, color: "#4338CA" } : {}}
                data-testid={`fmc-notes-topic-${t}`}
              >
                <span className="truncate">{t}</span>
                <span className="opacity-60">{n}</span>
              </button>
            ))}
          </div>
          <label className="text-xs flex items-center gap-1 cursor-pointer px-1">
            <input type="checkbox" checked={adminOnly} onChange={(e) => setAdminOnly(e.target.checked)} data-testid="fmc-notes-admin-only" />
            Admin notes only
          </label>
        </aside>

        <div className="md:col-span-3 space-y-2">
          {items == null && <div className="py-10 text-center opacity-60"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {items && filtered.length === 0 && <div className="wofy-card text-sm opacity-70">No notes match.</div>}
          {filtered.map((n) => (
            <button
              key={n.id}
              onClick={() => setSelected(n.id)}
              className="w-full text-left wofy-card hover:shadow-md transition"
              data-testid={`fmc-note-row-${n.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-slate-100">{n.topic}</span>
                    {n.is_admin_note && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">ADMIN</span>}
                    <span className="text-[11px] opacity-60">{new Date(n.note_date).toLocaleDateString()}</span>
                  </div>
                  <div className="font-medium">{n.title || n.details.slice(0, 80)}</div>
                  {n.title && <div className="text-sm opacity-80 line-clamp-2">{n.details}</div>}
                  <div className="text-[11px] opacity-60 mt-1">
                    by {n.author_name}
                    {n.member?.name && <> · about <b>{n.member.name}</b></>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 opacity-40 flex-shrink-0 mt-1" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {showForm && <NoteForm treeId={treeId} isAdmin={isAdmin} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {selected && <NoteDetail treeId={treeId} noteId={selected} isAdmin={isAdmin} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function NoteForm({ treeId, isAdmin, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    topic: initial?.topic || "General",
    title: initial?.title || "",
    details: initial?.details || "",
    note_date: initial?.note_date ? initial.note_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    member_id: initial?.member?.id || "",
    tags: (initial?.tags || []).join(", "),
    is_admin_note: !!initial?.is_admin_note,
  });
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get(`/family-trees/${treeId}/members?limit=1000`)
      .then((r) => setMembers(r.data?.items || []))
      .catch(() => setMembers([]));
  }, [treeId]);
  const upd = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.topic.trim().length < 1) return toast.error("Add a topic.");
    if (f.details.trim().length < 1) return toast.error("Add details.");
    setBusy(true);
    try {
      const payload = {
        topic: f.topic.trim(),
        title: f.title.trim() || undefined,
        details: f.details.trim(),
        note_date: f.note_date || undefined,
        member_id: f.member_id || undefined,
        tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
        is_admin_note: !!f.is_admin_note,
      };
      if (initial) {
        await http.patch(`/family-trees/${treeId}/notes/${initial.id}`, payload);
      } else {
        await http.post(`/family-trees/${treeId}/notes`, payload);
      }
      toast.success(initial ? "Note updated." : "Note saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title={initial ? "Edit note" : "New note"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="fmc-note-form">
        <div className="grid grid-cols-2 gap-2">
          <FormRow label="Topic *">
            <input className="wofy-input" value={f.topic} onChange={upd("topic")} placeholder="e.g. Legal, Health, Property" data-testid="fmc-note-topic" />
          </FormRow>
          <FormRow label="Date">
            <input type="date" className="wofy-input" value={f.note_date} onChange={upd("note_date")} data-testid="fmc-note-date" />
          </FormRow>
        </div>
        <FormRow label="Title (optional)">
          <input className="wofy-input" value={f.title} onChange={upd("title")} maxLength={240} data-testid="fmc-note-title" />
        </FormRow>
        <FormRow label="Related member (optional)">
          <select className="wofy-input" value={f.member_id} onChange={upd("member_id")} data-testid="fmc-note-member">
            <option value="">— none —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{[m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ") || m.nickname || m.id}</option>
            ))}
          </select>
        </FormRow>
        <FormRow label="Details *">
          <textarea dir="auto" className="wofy-input" rows={8} value={f.details} onChange={upd("details")} maxLength={20000} data-testid="fmc-note-details" />
        </FormRow>
        <FormRow label="Tags (comma-separated)">
          <input className="wofy-input" value={f.tags} onChange={upd("tags")} data-testid="fmc-note-tags" />
        </FormRow>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_admin_note} onChange={upd("is_admin_note")} data-testid="fmc-note-admin-flag" />
            Mark as admin note
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="fmc-note-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save note"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function NoteDetail({ treeId, noteId, isAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/notes/${noteId}`);
      setData(data);
    } catch { setData({ note: null }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [noteId]);
  const del = async () => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await http.delete(`/family-trees/${treeId}/notes/${noteId}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };
  if (!data) return <ModalShell title="Loading…" onClose={onClose}><Loader2 className="w-5 h-5 animate-spin mx-auto" /></ModalShell>;
  if (!data.note) return <ModalShell title="Not found" onClose={onClose}><p className="opacity-70">This note no longer exists.</p></ModalShell>;
  const n = data.note;
  if (editing) return <NoteForm treeId={treeId} isAdmin={isAdmin} initial={n} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />;
  return (
    <ModalShell title={n.title || n.topic} subtitle={new Date(n.note_date).toLocaleDateString()} onClose={onClose} widthClass="max-w-2xl">
      <div className="space-y-3" data-testid="fmc-note-detail">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-slate-100">{n.topic}</span>
          {n.is_admin_note && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">ADMIN</span>}
          {(n.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-50 text-amber-800"># {t}</span>)}
        </div>
        {n.member && (
          <div className="text-xs opacity-70">
            About <Link to={`/family/${treeId}/member/${n.member.id}`} className="underline">{n.member.name}</Link>
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap wofy-card" style={{ background: "#FAFAF7" }}>{n.details}</div>
        <div className="text-xs opacity-60">by {n.author_name}{n.author_is_admin ? " · admin" : ""} · {new Date(n.created_at).toLocaleString()}</div>
        {data.can_edit && (
          <div className="flex gap-2">
            <button onClick={() => setEditing(true)} className="wofy-btn-ghost text-xs" data-testid="fmc-note-edit">Edit</button>
            <button onClick={del} className="wofy-btn-ghost text-xs text-red-600" data-testid="fmc-note-del">Delete</button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

/* ============================================================
 *  Shared UI atoms
 * ============================================================ */
function ModalShell({ title, subtitle, children, onClose, widthClass = "max-w-xl" }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`wofy-card w-full ${widthClass} text-left max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display text-lg">{title}</h3>
            {subtitle && <div className="text-xs opacity-60 mt-0.5">{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} className="p-1 opacity-60 hover:opacity-100"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium mb-1 block opacity-70">{label}</label>
      {children}
    </div>
  );
}
